/**
 * Unified Sports Adapter
 * Provides a consistent interface for all sports services
 * Single source of truth for sports data fetching
 */

// Use RTDB-cached football service for better performance
import {
  rtdbFootballService as footballServiceExtended,
  cacheWarmer
} from '../../pages/sport-dashboards/football/rtdb';
import baseballService from '../../components/dashboards/services/baseballService';
import nflService from '../../components/dashboards/services/nflService';
import { rtdbNFLService } from '../../pages/sport-dashboards/NFL/rtdb';
import ncaaService from '../../components/dashboards/services/ncaaService';
import { rtdbNBAService as nbaService } from '../../pages/sport-dashboards/NBA/rtdb/rtdbNBAService';
import rtdbNHLService from '../../pages/sport-dashboards/NHL/rtdb/rtdbNHLService';
import { MAJOR_LEAGUE_IDS, LIVE_ODDS_PRIORITY_LEAGUES } from '../../components/dashboards/utils/majorLeagues';

/**
 * Sport types supported by the adapter
 */
export const SPORT_TYPES = {
  FOOTBALL: 'football',
  BASEBALL: 'baseball',
  NFL: 'nfl',
  NCAAF: 'ncaaf',
  NCAAB: 'ncaab',
  NBA: 'nba',
  NHL: 'nhl',
  F1: 'f1'
};

/**
 * Game status types across all sports
 */
export const GAME_STATUS = {
  NOT_STARTED: 'not_started',
  LIVE: 'live',
  FINISHED: 'finished',
  POSTPONED: 'postponed',
  CANCELLED: 'cancelled'
};

/**
 * Unified game data structure
 */
class UnifiedGame {
  constructor(data) {
    this.id = data.id;
    this.sport = data.sport;
    this.status = data.status;
    this.statusDetail = data.statusDetail;
    this.timestamp = data.timestamp;
    this.date = data.date;
    this.time = data.time;
    this.league = data.league;
    this.homeTeam = data.homeTeam;
    this.awayTeam = data.awayTeam;
    this.homeScore = data.homeScore;
    this.awayScore = data.awayScore;
    this.venue = data.venue;
    this.isUpcoming = data.isUpcoming;
    this.week = data.week;
    this.raw = data.raw; // Original API response for detailed views
  }

  isLive() {
    return this.status === GAME_STATUS.LIVE;
  }

  isFinished() {
    return this.status === GAME_STATUS.FINISHED;
  }

  isPending() {
    return this.status === GAME_STATUS.NOT_STARTED;
  }
}

/**
 * Sport-specific adapters
 */
class FootballAdapter {
  static async getFeaturedGames(filters = {}) {
    // The rtdbFootballService wraps footballServiceExtended which wraps FootballService
    // FootballService already initializes with the API key from env vars in its constructor
    // So we don't need to manually set it unless it's missing
    const apiKey = process.env.REACT_APP_FOOTBALL_API_KEY || '';
    
    // Only set API key if it's truly missing (which shouldn't happen normally)
    if (apiKey && footballServiceExtended.setApiKey && !footballServiceExtended.apiKey) {
      console.log('FootballAdapter: Setting API key manually');
      footballServiceExtended.setApiKey(apiKey);
    }

    // Warm cache on first load (same as dashboard)
    if (!this.cacheWarmed) {
      this.cacheWarmed = true;
      cacheWarmer.warmTodayFixtures().catch(err => 
        console.log('Cache warming in progress:', err)
      );
    }

    try {
      console.log('FootballAdapter.getFeaturedGames called with filters:', filters);
      console.log('FootballAdapter: API key available?', !!apiKey);
      const fixtures = await this.fetchFixtures(filters);
      console.log('FootballAdapter: fetchFixtures returned', fixtures?.length, 'fixtures');
      const normalized = this.normalizeFixtures(fixtures, filters);
      console.log('FootballAdapter: normalizeFixtures returned', normalized?.length, 'games');
      return normalized;
    } catch (error) {
      console.error('Football adapter error:', error);
      return [];
    }
  }

  static async fetchFixtures(filters) {
    const { date, leagueIds, live, status } = filters;
    // If status is 'live', set the live flag to true
    const fetchLive = live || status === 'live';
    let allFixtures = [];
    const seenIds = new Set();

    // Use today's date if not specified
    // The RTDB service proxies getTodayDate from the base service
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const targetDate = date || `${year}-${month}-${day}`;
    
    // Define popular leagues with priority weighting
    // Higher priority leagues should appear first when filtering
    const POPULAR_LEAGUES = [
      39,  // Premier League (priority: highest)
      140, // La Liga (priority: high)
      78,  // Bundesliga (priority: high)
      135, // Serie A (priority: high)
      61,  // Ligue 1 (priority: high)
      2,   // Champions League (priority: highest for matchdays)
      3,   // Europa League (priority: high for matchdays)
      253, // MLS (priority: medium)
      71,  // Brasileirao (priority: medium)
      262, // Liga MX (priority: medium)
    ];
    
    // League priority for sorting (higher = more important)
    const LEAGUE_PRIORITY = {
      2: 10,   // Champions League - highest for European nights
      39: 9,   // Premier League
      140: 8,  // La Liga
      78: 7,   // Bundesliga
      135: 7,  // Serie A
      61: 6,   // Ligue 1
      3: 6,    // Europa League
      253: 5,  // MLS
      71: 4,   // Brasileirao
      262: 4,  // Liga MX
    };

    // Use specified leagues or default to popular ones
    const leaguesToFetch = leagueIds && leagueIds.length > 0 ? leagueIds : POPULAR_LEAGUES;

    // Fetch live games if requested
    if (fetchLive) {
      try {
        const liveResponse = await footballServiceExtended.getFixtures(
          null, null, 'all', null, null,
          null, null, null, null, null, null, null, null, null, 'UTC'
        );
        const liveFixtures = (liveResponse.response || []).filter(f => {
          const status = f.fixture?.status?.short;
          const isLive = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(status);
          // Only include live games from our target leagues
          return isLive && leaguesToFetch.includes(f.league?.id);
        });
        liveFixtures.forEach(f => {
          if (!seenIds.has(f.fixture?.id)) {
            allFixtures.push(f);
            seenIds.add(f.fixture?.id);
          }
        });
      } catch (error) {
        console.log('Could not fetch live fixtures:', error);
      }
    }

    // Smart progressive loading: fetch today + tomorrow first, then progressively more if needed
    // Skip this if we're only fetching live games
    if (!date && !fetchLive) {
      const MAX_DAYS_TO_CHECK = 14;  // Extended to 14 days to find games
      const MIN_GAMES_NEEDED = 6;
      
      // Use the same date logic as the working football dashboard
      // Get today's date using the same method as FootballService
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const initialDates = [
        footballServiceExtended.getTodayDate(), // Use the same method as the dashboard
        tomorrow.toISOString().split('T')[0]
      ];
      
      console.log('🔍 FootballAdapter: Searching for fixtures on dates:', initialDates);
      console.log('🔍 FootballAdapter: Leagues to search:', leaguesToFetch);
      
      // Fetch today and tomorrow's games
      for (const dateStr of initialDates) {
        const leaguePromises = leaguesToFetch.map(async (leagueId) => {
          try {
            // For September 2025, all leagues use 2025
            // European leagues: 2025-2026 season = 2025 in API
            // Brazilian/MLS leagues: 2025 calendar year = 2025 in API
            const season = 2025;
            
            const response = await footballServiceExtended.getFixtures(
              null, null, null, dateStr, leagueId,
              season, null, null, null, null, null, null, null, null, 'UTC'
            );
            // The API returns {get: 'fixtures', parameters: {...}, results: N, response: [...]}
            if (response && response.response && Array.isArray(response.response)) {
              console.log(`League ${leagueId} on ${dateStr}: ${response.results} fixtures found`);
              return response.response;
            } else if (response && Array.isArray(response)) {
              // Fallback if response is directly an array
              console.log(`League ${leagueId} on ${dateStr}: ${response.length} fixtures found (direct array)`);
              return response;
            } else {
              console.log(`League ${leagueId} on ${dateStr}: No fixtures (results: ${response?.results || 0})`);
              return [];
            }
          } catch (error) {
            console.log(`Could not fetch fixtures for league ${leagueId} on ${dateStr}:`, error);
            return [];
          }
        });

        const leagueFixtures = await Promise.all(leaguePromises);
        leagueFixtures.flat().forEach(f => {
          if (!seenIds.has(f.fixture?.id)) {
            allFixtures.push(f);
            seenIds.add(f.fixture?.id);
          }
        });
      }
      
      // If we have enough games, stop here (saves API calls)
      const nonFinishedGames = allFixtures.filter(f => 
        !['FT', 'AET', 'PEN', 'PST', 'CANC', 'ABD', 'AWD', 'WO'].includes(f.fixture?.status?.short)
      );
      
      console.log(`After today+tomorrow: ${allFixtures.length} total games, ${nonFinishedGames.length} non-finished`);
      
      if (nonFinishedGames.length >= MIN_GAMES_NEEDED) {
        console.log(`Stopping early - found enough games (${nonFinishedGames.length} >= ${MIN_GAMES_NEEDED})`);
        return allFixtures;
      }
      
      // Otherwise, progressively check more days (day 3, 4, etc.)
      for (let i = 2; i < MAX_DAYS_TO_CHECK; i++) {
        const checkDate = new Date();
        checkDate.setDate(checkDate.getDate() + i);
        const dateStr = checkDate.toISOString().split('T')[0];
        
        const leaguePromises = leaguesToFetch.map(async (leagueId) => {
          try {
            // For September 2025, all leagues use 2025
            const season = 2025;
            const response = await footballServiceExtended.getFixtures(
              null, null, null, dateStr, leagueId,
              season, null, null, null, null, null, null, null, null, 'UTC'
            );
            // The API returns {get: 'fixtures', parameters: {...}, results: N, response: [...]}
            if (response && response.response && Array.isArray(response.response)) {
              console.log(`League ${leagueId} on ${dateStr}: ${response.results} fixtures found`);
              return response.response;
            } else if (response && Array.isArray(response)) {
              // Fallback if response is directly an array
              console.log(`League ${leagueId} on ${dateStr}: ${response.length} fixtures found (direct array)`);
              return response;
            } else {
              console.log(`League ${leagueId} on ${dateStr}: No fixtures (results: ${response?.results || 0})`);
              return [];
            }
          } catch (error) {
            console.log(`Could not fetch fixtures for league ${leagueId} on ${dateStr}:`, error);
            return [];
          }
        });

        const leagueFixtures = await Promise.all(leaguePromises);
        let foundNewGames = false;
        
        leagueFixtures.flat().forEach(f => {
          if (!seenIds.has(f.fixture?.id)) {
            allFixtures.push(f);
            seenIds.add(f.fixture?.id);
            foundNewGames = true;
          }
        });
        
        // Check if we now have enough non-finished games
        const currentNonFinished = allFixtures.filter(f => 
          !['FT', 'AET', 'PEN', 'PST', 'CANC', 'ABD', 'AWD', 'WO'].includes(f.fixture?.status?.short)
        );
        
        if (currentNonFinished.length >= MIN_GAMES_NEEDED) {
          console.log(`Found enough games (${currentNonFinished.length}) after checking ${i + 1} days`);
          break;
        }
        
        // Continue searching all days - don't stop early
        if (!foundNewGames) {
          console.log(`No games found on day ${i + 1}, continuing search...`);
        }
      }
    } else {
      // Single date fetch (when date is specified)
      const leaguePromises = leaguesToFetch.map(async (leagueId) => {
        try {
          // For September 2025, all leagues use 2025
          const season = 2025;
          const response = await footballServiceExtended.getFixtures(
            null, null, null, date, leagueId,
            season, null, null, null, null, null, null, null, null, 'UTC'
          );
          // The API returns {get: 'fixtures', parameters: {...}, results: N, response: [...]}
          if (response && response.response && Array.isArray(response.response)) {
            console.log(`League ${leagueId} on ${date}: ${response.results} fixtures found`);
            return response.response;
          } else if (response && Array.isArray(response)) {
            // Fallback if response is directly an array
            console.log(`League ${leagueId} on ${date}: ${response.length} fixtures found (direct array)`);
            return response;
          } else {
            console.log(`League ${leagueId} on ${date}: No fixtures (results: ${response?.results || 0})`);
            return [];
          }
        } catch (error) {
          console.log(`Could not fetch fixtures for league ${leagueId} on ${date}:`, error);
          return [];
        }
      });

      const leagueFixtures = await Promise.all(leaguePromises);
      leagueFixtures.flat().forEach(f => {
        if (!seenIds.has(f.fixture?.id)) {
          allFixtures.push(f);
          seenIds.add(f.fixture?.id);
        }
      });
    }

    console.log(`FootballAdapter.fetchFixtures returning ${allFixtures.length} total fixtures`);
    return allFixtures;
  }

  static normalizeFixtures(fixtures, filters) {
    // Keep a copy of all fixtures in case we filter them all out
    const allFixtures = [...fixtures];

    // If status filter is 'live', only show live games
    if (filters.status === 'live') {
      const liveStatuses = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'];
      fixtures = fixtures.filter(f =>
        liveStatuses.includes(f.fixture?.status?.short)
      );
    }
    // If status filter is 'upcoming', only show upcoming games
    else if (filters.status === 'upcoming') {
      const upcomingStatuses = ['TBD', 'NS', 'SUSP'];
      fixtures = fixtures.filter(f =>
        upcomingStatuses.includes(f.fixture?.status?.short)
      );
    }
    // Default: filter out finished games unless explicitly requested
    else if (!filters.includeFinished) {
      const finishedStatuses = ['FT', 'AET', 'PEN', 'PST', 'CANC', 'ABD', 'AWD', 'WO'];
      fixtures = fixtures.filter(f =>
        !finishedStatuses.includes(f.fixture?.status?.short)
      );
    }
    
    // League priority for sorting (defined here for access in normalization)
    const LEAGUE_PRIORITY = {
      2: 10,   // Champions League
      39: 9,   // Premier League
      140: 8,  // La Liga
      78: 7,   // Bundesliga
      135: 7,  // Serie A
      61: 6,   // Ligue 1
      3: 6,    // Europa League
      253: 5,  // MLS
      71: 4,   // Brasileirao
      262: 4,  // Liga MX
    };
    
    // Top teams that should be prioritized (team IDs)
    const TOP_TEAMS = [
      // Premier League
      33, 34, 40, 42, 47, 49, 50, // Man City, Newcastle, Liverpool, Arsenal, Tottenham, Chelsea, Man United
      // La Liga
      529, 530, 541, 543, 548, // Barcelona, Atletico, Real Madrid, Girona, Athletic
      // Bundesliga
      157, 165, 168, 169, 173, // Bayern, Dortmund, Leverkusen, Frankfurt, Leipzig
      // Serie A
      489, 492, 496, 497, 499, 505, // Milan, Napoli, Juventus, Roma, Atalanta, Inter
      // Ligue 1
      85, 91, 94, 106, // PSG, Monaco, Marseille, Lyon
    ];
    
    // Helper to check if match involves top teams
    const isTopMatch = (fixture) => {
      const homeId = fixture.teams?.home?.id;
      const awayId = fixture.teams?.away?.id;
      return TOP_TEAMS.includes(homeId) || TOP_TEAMS.includes(awayId);
    };

    // If we filtered out all games, show recent finished games instead of nothing
    if (fixtures.length === 0 && allFixtures.length > 0 && !filters.includeFinished) {
      console.log('No upcoming/live games found, showing recent games instead');
      fixtures = allFixtures; // Use all games including finished ones
    }

    // Enhanced sorting with league priority
    fixtures.sort((a, b) => {
      const aStatus = a.fixture?.status?.short;
      const bStatus = b.fixture?.status?.short;
      const aLeagueId = a.league?.id;
      const bLeagueId = b.league?.id;
      
      const aIsLive = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(aStatus);
      const bIsLive = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(bStatus);
      
      // 1. Live games always come first
      if (aIsLive && !bIsLive) return -1;
      if (!aIsLive && bIsLive) return 1;
      
      // 2. Within live games, prioritize by league importance
      if (aIsLive && bIsLive) {
        const aPriority = LEAGUE_PRIORITY[aLeagueId] || 0;
        const bPriority = LEAGUE_PRIORITY[bLeagueId] || 0;
        if (aPriority !== bPriority) return bPriority - aPriority;
      }
      
      // 2. Check if games are today
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      
      const aDate = new Date(a.fixture?.date);
      const bDate = new Date(b.fixture?.date);
      
      const aIsToday = aDate >= todayStart && aDate < todayEnd;
      const bIsToday = bDate >= todayStart && bDate < todayEnd;
      
      // Today's games come before future games
      if (aIsToday && !bIsToday) return -1;
      if (!aIsToday && bIsToday) return 1;
      
      // 3. Within non-live games, prioritize by league importance and top teams
      if (!aIsLive && !bIsLive) {
        // First check if either is a top match
        const aIsTop = isTopMatch(a);
        const bIsTop = isTopMatch(b);
        if (aIsTop && !bIsTop) return -1;
        if (!aIsTop && bIsTop) return 1;
        
        // Then by league priority
        const aPriority = LEAGUE_PRIORITY[aLeagueId] || 0;
        const bPriority = LEAGUE_PRIORITY[bLeagueId] || 0;
        if (aPriority !== bPriority) return bPriority - aPriority;
      }
      
      // 4. Finally sort by time
      const timeDiff = aDate - bDate;
      return timeDiff;
    });

    // Convert to unified format
    const unifiedGames = fixtures.slice(0, filters.limit || 8).map(f => this.toUnifiedGame(f));
    
    if (unifiedGames.length > 0) {
      console.log(`Returning ${unifiedGames.length} games (${unifiedGames.filter(g => g.isLive()).length} live, ${unifiedGames.filter(g => g.isPending()).length} upcoming, ${unifiedGames.filter(g => g.isFinished()).length} finished)`);
    } else {
      console.log('No games to display after normalization');
    }
    
    return unifiedGames;
  }

  static toUnifiedGame(fixture) {
    const status = this.getGameStatus(fixture.fixture?.status?.short);
    const gameDate = new Date(fixture.fixture?.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const gameDateOnly = new Date(gameDate);
    gameDateOnly.setHours(0, 0, 0, 0);

    return new UnifiedGame({
      id: fixture.fixture?.id,
      sport: SPORT_TYPES.FOOTBALL,
      status,
      statusDetail: fixture.fixture?.status?.short,
      timestamp: gameDate.getTime(),
      date: fixture.fixture?.date,
      time: this.formatTime(fixture.fixture?.date),
      league: {
        id: fixture.league?.id,
        name: fixture.league?.name,
        logo: fixture.league?.logo
      },
      homeTeam: {
        id: fixture.teams?.home?.id,
        name: fixture.teams?.home?.name,
        logo: fixture.teams?.home?.logo
      },
      awayTeam: {
        id: fixture.teams?.away?.id,
        name: fixture.teams?.away?.name,
        logo: fixture.teams?.away?.logo
      },
      homeScore: fixture.goals?.home,
      awayScore: fixture.goals?.away,
      venue: fixture.fixture?.venue?.name,
      isUpcoming: gameDateOnly > today,
      raw: fixture
    });
  }

  static getGameStatus(statusShort) {
    const liveStatuses = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'];
    const finishedStatuses = ['FT', 'AET', 'PEN'];
    const postponedStatuses = ['PST', 'CANC', 'ABD', 'AWD', 'WO'];

    if (liveStatuses.includes(statusShort)) return GAME_STATUS.LIVE;
    if (finishedStatuses.includes(statusShort)) return GAME_STATUS.FINISHED;
    if (postponedStatuses.includes(statusShort)) return GAME_STATUS.POSTPONED;
    return GAME_STATUS.NOT_STARTED;
  }

  static formatTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const gameDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Format time
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const timeStr = `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    
    // Add date context for homepage cards
    if (gameDay.getTime() === today.getTime()) {
      return `Today ${timeStr}`;
    } else if (gameDay.getTime() === tomorrow.getTime()) {
      return `Tomorrow ${timeStr}`;
    } else {
      // Show day of week for games within the next week
      const daysUntil = Math.floor((gameDay - today) / (1000 * 60 * 60 * 24));
      if (daysUntil > 0 && daysUntil <= 7) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `${days[date.getDay()]} ${timeStr}`;
      } else {
        // For games further out, show the date
        const month = (date.getMonth() + 1).toString();
        const day = date.getDate().toString();
        return `${month}/${day} ${timeStr}`;
      }
    }
  }

  static getTomorrowDate() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }

  static getNext7Days() {
    // Get dates for next 7 days to ensure content is always shown
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }

  static getNextWeekDates() {
    // Deprecated - only fetch 2 days for performance
    const dates = [];
    for (let i = 0; i < 2; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }
}

class BaseballAdapter {
  static async getFeaturedGames(filters = {}) {
    const apiKey = process.env.REACT_APP_FOOTBALL_API_KEY || '';
    if (!baseballService.apiKey) {
      baseballService.setApiKey(apiKey);
    }

    try {
      // If specific date provided, use it. Otherwise fetch today + next 2 days
      const { date } = filters;
      const allGames = [];
      const seenIds = new Set();

      if (date) {
        // Single date fetch
        const response = await baseballService.getGames(
          null, date, 1, 2024, null, 'America/New_York'
        );
        const games = response?.response || [];
        games.forEach(g => {
          if (!seenIds.has(g.id)) {
            allGames.push(g);
            seenIds.add(g.id);
          }
        });
      } else {
        // Fetch today + next 2 days to show more games
        const DAYS_TO_FETCH = 3; // Today + 2 days ahead
        const today = new Date();

        for (let i = 0; i < DAYS_TO_FETCH; i++) {
          const checkDate = new Date(today);
          checkDate.setDate(today.getDate() + i);
          const year = checkDate.getFullYear();
          const month = String(checkDate.getMonth() + 1).padStart(2, '0');
          const day = String(checkDate.getDate()).padStart(2, '0');
          const dateStr = `${year}-${month}-${day}`;

          // Determine season based on month
          // MLB season runs April-October, so Oct-Mar use previous year
          let season = year;
          const monthNum = checkDate.getMonth(); // 0-11
          if (monthNum >= 10 || monthNum <= 2) { // Oct, Nov, Dec, Jan, Feb, Mar
            season = year - 1;
          }

          try {
            const response = await baseballService.getGames(
              null, dateStr, 1, season, null, 'America/New_York'
            );
            const games = response?.response || [];
            console.log(`MLB games on ${dateStr} (season ${season}): ${games.length} found`);

            games.forEach(g => {
              if (!seenIds.has(g.id)) {
                allGames.push(g);
                seenIds.add(g.id);
              }
            });
          } catch (err) {
            console.log(`No MLB games on ${dateStr}:`, err.message);
          }
        }
      }

      return this.normalizeGames(allGames, filters);
    } catch (error) {
      console.error('Baseball adapter error:', error);
      return [];
    }
  }

  static normalizeGames(games, filters) {
    // Convert to unified format
    const unifiedGames = games.map(g => this.toUnifiedGame(g));

    // Sort by status
    unifiedGames.sort((a, b) => {
      if (a.isLive() && !b.isLive()) return -1;
      if (!a.isLive() && b.isLive()) return 1;
      if (!a.isFinished() && b.isFinished()) return -1;
      if (a.isFinished() && !b.isFinished()) return 1;
      return a.timestamp - b.timestamp;
    });

    return unifiedGames.slice(0, filters.limit || 8);
  }

  static toUnifiedGame(game) {
    const status = this.getGameStatus(game.status?.short);
    const formattedTime = game.date ? baseballService.formatTime(game.date) : game.time;

    return new UnifiedGame({
      id: game.id,
      sport: SPORT_TYPES.BASEBALL,
      status,
      statusDetail: game.status?.short,
      timestamp: new Date(game.date).getTime(),
      date: game.date,
      time: formattedTime,
      league: {
        id: game.league?.id,
        name: game.league?.name || 'MLB',
        logo: game.league?.logo
      },
      homeTeam: {
        id: game.teams?.home?.id,
        name: game.teams?.home?.name,
        logo: game.teams?.home?.logo
      },
      awayTeam: {
        id: game.teams?.away?.id,
        name: game.teams?.away?.name,
        logo: game.teams?.away?.logo
      },
      homeScore: game.scores?.home?.total,
      awayScore: game.scores?.away?.total,
      venue: game.venue?.name,
      isUpcoming: status === GAME_STATUS.NOT_STARTED,
      raw: game
    });
  }

  static getGameStatus(statusShort) {
    const liveStatuses = ['IN1', 'IN2', 'IN3', 'IN4', 'IN5', 'IN6', 'IN7', 'IN8', 'IN9', 'LIVE', 'IN_PLAY'];
    const finishedStatuses = ['FT', 'AOT', 'FINISHED'];
    const postponedStatuses = ['POST', 'CANC', 'INTR', 'ABD'];

    if (liveStatuses.includes(statusShort) || (statusShort && statusShort.startsWith('IN'))) {
      return GAME_STATUS.LIVE;
    }
    if (finishedStatuses.includes(statusShort)) return GAME_STATUS.FINISHED;
    if (postponedStatuses.includes(statusShort)) return GAME_STATUS.POSTPONED;
    return GAME_STATUS.NOT_STARTED;
  }
}

class NFLAdapter {
  static async getFeaturedGames(filters = {}) {
    try {
      // Auto-detect current NFL week if not provided
      const { getCurrentNFLWeek } = await import('../../pages/sport-dashboards/NFL/utils/nflWeekDetector');
      const currentWeekInfo = getCurrentNFLWeek();

      // Use current week detected by nflWeekDetector
      // This handles both regular season and playoffs correctly
      let targetWeek = currentWeekInfo.weekId;
      let targetSeason = currentWeekInfo.seasonYear;

      if (!filters.week) {
        console.log(`[NFLAdapter] Using detected week: ${targetWeek} (week ${currentWeekInfo.weekNumber}) for season ${targetSeason}`);
      }

      const { week = targetWeek, season = targetSeason } = filters;

      console.log(`🏈 [NFLAdapter] Fetching NFL games using RTDB service:`, {
        week,
        season
      });

      // Use RTDB service (same as working NFL dashboard)
      const response = await rtdbNFLService.getGamesForWeek(
        week,
        season,
        1, // NFL league ID
        null, // teamId
        'America/New_York'
      );

      const allGames = response?.response || [];
      console.log(`🏈 [NFLAdapter] RTDB returned ${allGames.length} games`);

      const normalized = this.normalizeGames(allGames, filters, week);
      console.log(`🏈 [NFLAdapter] Returning ${normalized.length} normalized games`);
      return normalized;
    } catch (error) {
      console.error('NFL adapter error:', error);
      return [];
    }
  }

  static normalizeGames(games, filters, week) {
    const unifiedGames = games.map(g => this.toUnifiedGame(g, week));

    // Sort by status priority: Live > Upcoming > Finished
    unifiedGames.sort((a, b) => {
      // Live games first
      if (a.isLive() && !b.isLive()) return -1;
      if (!a.isLive() && b.isLive()) return 1;

      // Then upcoming games
      if (a.isPending() && !b.isPending()) return -1;
      if (!a.isPending() && b.isPending()) return 1;

      // Then finished games (least priority)
      if (a.isFinished() && !b.isFinished()) return 1;
      if (!a.isFinished() && b.isFinished()) return -1;

      // Within same status, sort by timestamp
      return a.timestamp - b.timestamp;
    });

    return unifiedGames.slice(0, filters.limit || 8);
  }

  static toUnifiedGame(game, week) {
    const gameDate = game.game?.date || game.date;
    const status = this.getGameStatus(game.game?.status?.short || 'NS');
    let formattedTime = null;

    if (gameDate) {
      try {
        const date = new Date(gameDate);
        if (!isNaN(date.getTime())) {
          const hours = date.getHours();
          const minutes = date.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          formattedTime = `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm} ET`;
        }
      } catch (err) {
        console.log('Error formatting NFL time:', err);
      }
    }

    // Preserve original nested structure for components that need it (xPicks, predictions)
    // This maintains backward compatibility with working NFL dashboard cards
    return new UnifiedGame({
      id: game.game?.id || game.id,
      sport: SPORT_TYPES.NFL,
      status,
      statusDetail: game.game?.status?.short || 'NS',
      timestamp: new Date(gameDate).getTime(),
      date: gameDate,
      time: formattedTime,
      league: {
        id: game.league?.id || 1,
        name: game.league?.name || 'NFL',
        logo: game.league?.logo
      },
      homeTeam: {
        id: game.teams?.home?.id,
        name: game.teams?.home?.name,
        logo: game.teams?.home?.logo
      },
      awayTeam: {
        id: game.teams?.away?.id,
        name: game.teams?.away?.name,
        logo: game.teams?.away?.logo
      },
      homeScore: game.scores?.home?.total,
      awayScore: game.scores?.away?.total,
      venue: game.venue?.name,
      isUpcoming: status === GAME_STATUS.NOT_STARTED,
      // Keep week in weekId format for filtering/matching (e.g., "week11")
      week: week || null,
      // Preserve original nested structure for components that need raw API format
      game: game.game, // Nested game object with date.date, date.time, status, etc.
      teams: game.teams, // Preserve teams structure for compatibility
      scores: game.scores, // Preserve scores structure for compatibility
      raw: game // Full original API response for maximum compatibility
    });
  }

  static getGameStatus(statusShort) {
    const liveStatuses = ['Q1', 'Q2', 'Q3', 'Q4', 'HT', 'OT', 'IN_PLAY'];
    const finishedStatuses = ['FT', 'AOT'];
    const postponedStatuses = ['PST', 'CANC'];

    if (liveStatuses.includes(statusShort)) return GAME_STATUS.LIVE;
    if (finishedStatuses.includes(statusShort)) return GAME_STATUS.FINISHED;
    if (postponedStatuses.includes(statusShort)) return GAME_STATUS.POSTPONED;
    return GAME_STATUS.NOT_STARTED;
  }
}

class NCAAFAdapter {
  static async getFeaturedGames(filters = {}) {
    const apiKey = process.env.REACT_APP_FOOTBALL_API_KEY || '';
    if (!ncaaService.apiKey) {
      ncaaService.setApiKey(apiKey);
    }

    try {
      // Get current date or use filter date
      const { date, conference, season = 2025, limit = 8 } = filters;
      const targetDate = date || ncaaService.getTodayDate();
      
      // For NCAAF, we fetch games by date since there's no simple week system like NFL
      // Multiple conferences play on different days
      const gamesResponse = await ncaaService.getGamesForSeasons(
        targetDate,
        [2025, 2024, 2023], // Check multiple seasons for better coverage
        2, // League ID for NCAA
        null, // team
        'America/New_York' // timezone
      );
      
      let games = gamesResponse.response || [];
      
      // Filter by conference if specified
      if (conference) {
        games = games.filter(game => {
          const homeConf = game.teams?.home?.conference;
          const awayConf = game.teams?.away?.conference;
          const confUpper = conference.toUpperCase();
          return (homeConf && homeConf.toUpperCase().includes(confUpper)) || 
                 (awayConf && awayConf.toUpperCase().includes(confUpper));
        });
      }
      
      // If no games on target date, look ahead up to 7 days
      if (games.length === 0 && !date) {
        const MAX_DAYS_AHEAD = 7;
        for (let i = 1; i <= MAX_DAYS_AHEAD && games.length === 0; i++) {
          const nextDate = new Date();
          nextDate.setDate(nextDate.getDate() + i);
          const year = nextDate.getFullYear();
          const month = String(nextDate.getMonth() + 1).padStart(2, '0');
          const day = String(nextDate.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          
          try {
            const nextResponse = await ncaaService.getGamesForSeasons(
              dateString,
              [2025, 2024, 2023],
              2,
              null,
              'America/New_York'
            );
            games = nextResponse.response || [];
            if (conference && games.length > 0) {
              games = games.filter(game => {
                const homeConf = game.teams?.home?.conference;
                const awayConf = game.teams?.away?.conference;
                const confUpper = conference.toUpperCase();
                return (homeConf && homeConf.toUpperCase().includes(confUpper)) || 
                       (awayConf && awayConf.toUpperCase().includes(confUpper));
              });
            }
          } catch (err) {
            console.log(`No NCAAF games on day ${i} ahead`);
          }
        }
      }

      return this.normalizeGames(games, filters);
    } catch (error) {
      console.error('NCAAF adapter error:', error);
      return [];
    }
  }

  static normalizeGames(games, filters) {
    const unifiedGames = games.map(g => this.toUnifiedGame(g));

    // Sort by status and date (live > upcoming > finished)
    unifiedGames.sort((a, b) => {
      // Live games first
      if (a.isLive() && !b.isLive()) return -1;
      if (!a.isLive() && b.isLive()) return 1;
      
      // Then upcoming games
      if (!a.isFinished() && b.isFinished()) return -1;
      if (a.isFinished() && !b.isFinished()) return 1;
      
      // Finally by timestamp
      return a.timestamp - b.timestamp;
    });

    return unifiedGames.slice(0, filters.limit || 8);
  }

  static toUnifiedGame(game) {
    const gameInfo = game.game || {};
    const gameDate = gameInfo.date?.date && gameInfo.date?.time 
      ? `${gameInfo.date.date}T${gameInfo.date.time}`
      : gameInfo.date;
    
    const status = this.getGameStatus(gameInfo.status?.short || 'NS');
    let formattedTime = null;

    if (gameDate) {
      try {
        formattedTime = ncaaService.formatTime(gameDate);
      } catch (err) {
        console.log('Error formatting NCAAF time:', err);
        formattedTime = 'TBD';
      }
    }

    // Extract rankings from team names if present (e.g., "#1 Georgia")
    const extractRanking = (teamName) => {
      const match = teamName?.match(/^#(\d+)\s/);
      return match ? match[1] : null;
    };

    const cleanTeamName = (teamName) => {
      return teamName?.replace(/^#\d+\s/, '') || 'Unknown';
    };

    return new UnifiedGame({
      id: gameInfo.id || game.id,
      sport: SPORT_TYPES.NCAAF,
      status,
      statusDetail: gameInfo.status?.short || 'NS',
      timestamp: gameDate ? new Date(gameDate).getTime() : 0,
      date: gameDate,
      time: formattedTime,
      league: {
        id: game.league?.id || 2,
        name: 'NCAA Football',
        logo: game.league?.logo
      },
      homeTeam: {
        id: game.teams?.home?.id,
        name: cleanTeamName(game.teams?.home?.name),
        logo: game.teams?.home?.logo,
        ranking: extractRanking(game.teams?.home?.name),
        conference: game.teams?.home?.conference
      },
      awayTeam: {
        id: game.teams?.away?.id,
        name: cleanTeamName(game.teams?.away?.name),
        logo: game.teams?.away?.logo,
        ranking: extractRanking(game.teams?.away?.name),
        conference: game.teams?.away?.conference
      },
      homeScore: typeof game.scores?.home === 'object' && game.scores?.home?.total !== undefined 
        ? game.scores.home.total 
        : (typeof game.scores?.home === 'number' ? game.scores.home : 0),
      awayScore: typeof game.scores?.away === 'object' && game.scores?.away?.total !== undefined 
        ? game.scores.away.total 
        : (typeof game.scores?.away === 'number' ? game.scores.away : 0),
      venue: game.venue?.name,
      isUpcoming: status === GAME_STATUS.NOT_STARTED,
      raw: game
    });
  }

  static getGameStatus(statusShort) {
    const liveStatuses = ['IN PLAY', 'LIVE', 'Q1', 'Q2', 'Q3', 'Q4', 'HT', 'OT'];
    const finishedStatuses = ['FT', 'AOT'];
    const postponedStatuses = ['PST', 'CANC', 'POST'];

    if (liveStatuses.includes(statusShort) || (statusShort && statusShort.startsWith('Q'))) {
      return GAME_STATUS.LIVE;
    }
    if (finishedStatuses.includes(statusShort)) return GAME_STATUS.FINISHED;
    if (postponedStatuses.includes(statusShort)) return GAME_STATUS.POSTPONED;
    return GAME_STATUS.NOT_STARTED;
  }
}

/**
 * NBA Adapter
 * Fetches and normalizes NBA games for the unified sports feed
 * NBA has daily games year-round (Oct-June), making it simpler than NFL's weekly schedule
 */
class NBAAdapter {
  static async getFeaturedGames(filters = {}) {
    try {
      // Extract filter parameters - NBA is simple, just needs date and limit
      const { date, season = 2025, limit = 8 } = filters;

      // Get today's date in local timezone if no date specified
      const targetDate = date || nbaService.getTodayDate();

      // Fetch games using multi-season approach (same pattern as NFL/NCAAF)
      // This handles cases where we're in the off-season or between seasons
      const gamesResponse = await nbaService.getGamesForSeasons(
        targetDate,           // Date to query (YYYY-MM-DD format)
        [2025, 2024, 2023],  // Seasons to try (API uses season start year)
        'standard',           // League type (standard NBA games)
        null,                 // Team filter (null = all teams)
        'America/New_York'    // Timezone for ET display
      );

      // Extract games array from response
      const games = gamesResponse.response || [];

      // Normalize to unified format and apply sorting/filtering
      return this.normalizeGames(games, filters);
    } catch (error) {
      console.error('NBA adapter error:', error);
      return [];
    }
  }

  static normalizeGames(games, filters) {
    // Convert all NBA games to unified format
    const unifiedGames = games.map(g => this.toUnifiedGame(g));

    // Sort by priority: Live games first, then upcoming, then finished
    // Within each category, sort by timestamp (earlier games first)
    unifiedGames.sort((a, b) => {
      // 1. Live games always come first
      if (a.isLive() && !b.isLive()) return -1;
      if (!a.isLive() && b.isLive()) return 1;

      // 2. Upcoming games come before finished games
      if (!a.isFinished() && b.isFinished()) return -1;
      if (a.isFinished() && !b.isFinished()) return 1;

      // 3. Within same status, sort by game time
      return a.timestamp - b.timestamp;
    });

    // Apply limit (default 8 games for homepage feed)
    return unifiedGames.slice(0, filters.limit || 8);
  }

  static toUnifiedGame(game) {
    // Determine game status using NBA's numeric status codes
    const status = this.getGameStatus(game.status?.short);

    // NBA API returns game start time in date.start field
    const gameDate = game.date?.start;
    let formattedTime = null;

    // Format time for display (e.g., "7:30 PM ET")
    if (gameDate) {
      try {
        const date = new Date(gameDate);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        formattedTime = `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm} ET`;
      } catch (err) {
        console.log('Error formatting NBA time:', err);
      }
    }

    // Return normalized game in UnifiedGame format
    return new UnifiedGame({
      id: game.id,
      sport: SPORT_TYPES.NBA,
      status,
      statusDetail: game.status?.short, // Keep raw status for debugging
      timestamp: gameDate ? new Date(gameDate).getTime() : 0,
      date: gameDate,
      time: formattedTime,
      league: {
        id: game.league?.id || 12, // 12 is NBA league ID in API
        name: 'NBA',
        logo: game.league?.logo
      },
      homeTeam: {
        id: game.teams?.home?.id,
        name: game.teams?.home?.name,
        logo: game.teams?.home?.logo
      },
      awayTeam: {
        // Note: NBA API uses "visitors" instead of "away"
        id: game.teams?.visitors?.id,
        name: game.teams?.visitors?.name,
        logo: game.teams?.visitors?.logo
      },
      homeScore: game.scores?.home?.points,
      awayScore: game.scores?.visitors?.points, // Also uses "visitors" for scores
      venue: game.arena?.name, // NBA uses "arena" field instead of "venue"
      isUpcoming: status === GAME_STATUS.NOT_STARTED,
      raw: game // Keep original API response for detailed views
    });
  }

  static getGameStatus(statusShort) {
    // NBA API uses numeric status codes (not strings like other sports)
    // Reference: 1=Not Started, 2=Live/In Progress, 3=Finished
    // Additional: 4=Postponed, 5=Delayed, 6=Canceled

    if (statusShort === 2) return GAME_STATUS.LIVE;       // Game is currently in progress
    if (statusShort === 3) return GAME_STATUS.FINISHED;   // Game has ended
    if (statusShort === 4 || statusShort === 5 || statusShort === 6) {
      return GAME_STATUS.POSTPONED; // Postponed/Delayed/Canceled
    }
    return GAME_STATUS.NOT_STARTED; // Default: game hasn't started yet (status = 1)
  }
}

/**
 * NHL Adapter
 * Handles NHL hockey games from api-sports.io
 */
class NHLAdapter {
  static async getFeaturedGames(filters = {}) {
    try {
      // Extract filter parameters
      const { date, limit = 8 } = filters;

      // Get today's date if no date specified
      const targetDate = date || this.getTodayDate();

      console.log('🏒 NHLAdapter: Fetching games for date:', targetDate);

      // Fetch games using RTDB-cached service
      // NHL API uses 4-digit season (e.g., 2025 for 2025-26 season)
      // For Nov 2025, we're in the 2025-26 season, so use 2025
      const gamesResponse = await rtdbNHLService.getGames({
        date: targetDate,
        league: 57, // NHL league ID
        season: 2025, // 4-digit year format (2025-26 season = 2025)
        timezone: 'America/New_York'
      });

      console.log('🏒 NHLAdapter: Raw response:', gamesResponse);

      // Handle both response formats:
      // 1. Direct array from cache: [game1, game2, ...]
      // 2. API response object: { response: [...], results: X }
      const games = Array.isArray(gamesResponse)
        ? gamesResponse
        : (gamesResponse.response || []);

      console.log('🏒 NHLAdapter: Games array:', games);
      console.log('🏒 NHLAdapter: Games count:', games.length);

      // Normalize to unified format and apply sorting/filtering
      const normalized = this.normalizeGames(games, filters);
      console.log('🏒 NHLAdapter: Normalized games:', normalized.length, normalized);
      return normalized;
    } catch (error) {
      console.error('NHL adapter error:', error);
      return [];
    }
  }

  static getTodayDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  static normalizeGames(games, filters) {
    // Convert all NHL games to unified format
    const unifiedGames = games.map(g => this.toUnifiedGame(g));

    // Sort by priority: Live games first, then upcoming, then finished
    unifiedGames.sort((a, b) => {
      // 1. Live games always come first
      if (a.isLive() && !b.isLive()) return -1;
      if (!a.isLive() && b.isLive()) return 1;

      // 2. Upcoming games come before finished games
      if (!a.isFinished() && b.isFinished()) return -1;
      if (a.isFinished() && !b.isFinished()) return 1;

      // 3. Within same status, sort by game time
      return a.timestamp - b.timestamp;
    });

    // Apply limit (default 8 games for homepage feed)
    return unifiedGames.slice(0, filters.limit || 8);
  }

  static toUnifiedGame(game) {
    // Determine game status
    const status = this.getGameStatus(game.status?.short);

    // NHL API returns game start time in date field
    const gameDate = game.date;
    let formattedTime = null;

    // Format time for display
    if (gameDate) {
      try {
        const date = new Date(gameDate);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        formattedTime = `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm} ET`;
      } catch (err) {
        console.log('Error formatting NHL time:', err);
      }
    }

    // Return normalized game in UnifiedGame format
    return new UnifiedGame({
      id: game.id,
      sport: SPORT_TYPES.NHL,
      status,
      statusDetail: game.status?.short,
      timestamp: gameDate ? new Date(gameDate).getTime() : 0,
      date: gameDate,
      time: formattedTime,
      league: {
        id: game.league?.id || 57, // 57 is NHL league ID in API
        name: 'NHL',
        logo: game.league?.logo
      },
      homeTeam: {
        id: game.teams?.home?.id,
        name: game.teams?.home?.name,
        logo: game.teams?.home?.logo
      },
      awayTeam: {
        id: game.teams?.away?.id,
        name: game.teams?.away?.name,
        logo: game.teams?.away?.logo
      },
      homeScore: game.scores?.home,
      awayScore: game.scores?.away,
      venue: game.venue || game.arena,
      raw: game
    });
  }

  static getGameStatus(statusShort) {
    // NHL uses string status codes similar to NBA
    // Common values: 'NS' (Not Started), 'LIVE', 'FT' (Full Time), 'AOT' (After OT), 'AP' (After Penalties)
    if (!statusShort) return GAME_STATUS.NOT_STARTED;

    const status = String(statusShort).toUpperCase();

    if (status === 'LIVE' || status.includes('P') || status.includes('OT')) {
      return GAME_STATUS.LIVE;
    }
    if (status === 'FT' || status === 'AOT' || status === 'AP' || status === 'FINAL') {
      return GAME_STATUS.FINISHED;
    }
    if (status === 'PST' || status === 'CANC' || status === 'SUSP') {
      return GAME_STATUS.POSTPONED;
    }
    return GAME_STATUS.NOT_STARTED;
  }
}

/**
 * Main Unified Sports Adapter
 */
export class UnifiedSportsAdapter {
  static async getFeaturedGames(sport, filters = {}) {
    switch (sport) {
      case SPORT_TYPES.FOOTBALL:
        return FootballAdapter.getFeaturedGames(filters);
      case SPORT_TYPES.BASEBALL:
        return BaseballAdapter.getFeaturedGames(filters);
      case SPORT_TYPES.NFL:
        return NFLAdapter.getFeaturedGames(filters);
      case SPORT_TYPES.NCAAF:
        return NCAAFAdapter.getFeaturedGames(filters);
      case SPORT_TYPES.NBA:
        return NBAAdapter.getFeaturedGames(filters);
      case SPORT_TYPES.NHL:
        return NHLAdapter.getFeaturedGames(filters);
      default:
        console.warn(`Unsupported sport type: ${sport}`);
        return [];
    }
  }

  static async getMultiSportGames(sports = [SPORT_TYPES.FOOTBALL, SPORT_TYPES.BASEBALL, SPORT_TYPES.NFL, SPORT_TYPES.NCAAF, SPORT_TYPES.NBA], filters = {}) {
    const promises = sports.map(sport => 
      this.getFeaturedGames(sport, filters).catch(err => {
        console.error(`Error fetching ${sport} games:`, err);
        return [];
      })
    );

    const results = await Promise.all(promises);
    const allGames = results.flat();

    // Sort all games by priority (live first, then by time)
    allGames.sort((a, b) => {
      if (a.isLive() && !b.isLive()) return -1;
      if (!a.isLive() && b.isLive()) return 1;
      return a.timestamp - b.timestamp;
    });

    return allGames.slice(0, filters.limit || 24);
  }

  static filterGames(games, filters) {
    let filtered = [...games];

    if (filters.status) {
      switch (filters.status) {
        case 'live':
          filtered = filtered.filter(g => g.isLive());
          break;
        case 'upcoming':
          filtered = filtered.filter(g => g.isPending());
          break;
        case 'finished':
          filtered = filtered.filter(g => g.isFinished());
          break;
      }
    }

    if (filters.leagueId) {
      filtered = filtered.filter(g => g.league.id === filters.leagueId);
    }

    if (filters.sport) {
      filtered = filtered.filter(g => g.sport === filters.sport);
    }

    return filtered;
  }
}

export default UnifiedSportsAdapter;